import { clearAllForLogout } from '../lib/db/schema.js';
import { create } from 'zustand';
import type { PublicUser, LoginVerifyResponse } from '@jaluzi/types';
import { persistent, ephemeral, clearAuth } from '../lib/storage.js';
import { forgetOfflineUnlock } from '../lib/offline-unlock.js';
import { api, setAccessToken } from '../lib/api.js';
import { queryClient } from '../lib/queryClient.js';
import { usePrefs } from './prefs.js';

type AuthStatus =
  | 'booting'
  | 'unauthenticated'
  | 'pin_required'
  | 'authenticated';

/** Троттл для refreshMe (см. ниже) — общий на модуль. */
let lastMeRefreshAt = 0;

interface AuthState {
  status: AuthStatus;
  user: PublicUser | null;
  error: string | null;

  init: () => Promise<void>;
  applyLogin: (data: LoginVerifyResponse) => void;
  /** Обновить данные текущего пользователя (после смены аватара/имени и т.п.) */
  setUser: (user: PublicUser) => void;
  /** Перечитать профиль с сервера и подтянуть его UI-настройки (без перезагрузки).
   *  Дёргается при возврате в приложение — чтобы стиль формы замера / вид карточки,
   *  изменённые на другом устройстве, применились здесь. */
  refreshMe: () => Promise<void>;
  unlockSession: () => void;
  /** Мягкая блокировка — на экран PIN, HttpOnly refresh-cookie сохраняем. */
  lockSession: () => void;
  /** Полный выход — стирает сессию, нужен Telegram-код заново. */
  logout: () => Promise<void>;
  /** Сервер уже отклонил refresh: локальная очистка без повторного logout. */
  expireSession: () => void;
  setError: (msg: string | null) => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'booting',
  user: persistent.getUser<PublicUser>(),
  error: null,

  init: async () => {
    const cachedUser = persistent.getUser<PublicUser>();

    try {
      const r = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client': 'field-app' },
        credentials: 'same-origin',
        body: '{}',
      });
      if (r.status === 401 || r.status === 403) {
        clearAuth();
        set({ status: 'unauthenticated', user: null });
        return;
      }
      if (!r.ok) throw new Error(`Auth refresh failed: ${r.status}`);
      const data = (await r.json()) as { accessToken: string };
      setAccessToken(data.accessToken);

      const me = await api<PublicUser>('/v1/auth/me');
      persistent.setUser(me);
      // Онлайн-старт: сервер — источник истины для UI-настроек, которые
      // синхронизируются между устройствами (стиль формы замера, вид карточки).
      usePrefs.getState().hydrateFromUser(me);

      // Нет ни PIN, ни Face ID — блокировать нечем, пускаем сразу. Если хотя бы один
      // способ есть, показываем экран разблокировки (PinPage умеет и biometric-only).
      if (!me.hasPin && !me.hasWebauthn) ephemeral.unlockPin();

      const unlocked = ephemeral.isPinUnlocked();
      set({
        status: unlocked ? 'authenticated' : 'pin_required',
        user: me,
      });
    } catch {
      // Сеть упала — пускаем по кешу, чтобы офлайн-режим работал
      if (cachedUser) {
        // Как и в онлайн-ветке: нечем блокировать — пускаем сразу, иначе без сети
        // сотрудник без PIN и Face ID застрял бы на экране разблокировки.
        if (!cachedUser.hasPin && !cachedUser.hasWebauthn) ephemeral.unlockPin();
        const unlocked = ephemeral.isPinUnlocked();
        set({
          status: unlocked ? 'authenticated' : 'pin_required',
          user: cachedUser,
        });
      } else {
        set({ status: 'unauthenticated', user: null });
      }
    }
  },

  applyLogin: (data) => {
    /* Очищаем React Query кеш — иначе при смене пользователя (особенно в
       демо-режиме когда быстро переключаются роли) экраны какое-то время
       показывают данные предыдущего юзера до фонового refetch'а. */
    queryClient.clear();
    setAccessToken(data.accessToken);
    persistent.setUser(data.user);
    // Свежий вход — подтягиваем сохранённые на сервере UI-настройки сотрудника.
    usePrefs.getState().hydrateFromUser(data.user);
    ephemeral.unlockPin();
    set({ status: 'authenticated', user: data.user, error: null });
  },

  setUser: (user) => {
    persistent.setUser(user);
    set({ user });
  },

  refreshMe: async () => {
    // Только для активной сессии; троттлим, чтобы парный focus+visibilitychange
    // не бил по /auth/me дважды подряд.
    if (get().status !== 'authenticated') return;
    const now = Date.now();
    if (now - lastMeRefreshAt < 2000) return;
    lastMeRefreshAt = now;
    try {
      const me = await api<PublicUser>('/v1/auth/me');
      persistent.setUser(me);
      usePrefs.getState().hydrateFromUser(me);
      set({ user: me });
    } catch {
      // Офлайн/ошибка — молча остаёмся на текущих значениях.
    }
  },

  unlockSession: () => {
    ephemeral.unlockPin();
    set({ status: 'authenticated' });
  },

  lockSession: () => {
    ephemeral.resetPin();
    const u = get().user;
    // Разблокировать сессию нечем (ни PIN, ни Face ID) — только полный логаут.
    if (!u?.hasPin && !u?.hasWebauthn) {
      void get().logout();
      return;
    }
    set({ status: 'pin_required' });
  },

  logout: async () => {
    try {
      await api('/v1/auth/logout', { method: 'POST', authRefresh: false });
    } catch { /* ignore */ }
    get().expireSession();
  },

  expireSession: () => {
    clearAuth();
    forgetOfflineUnlock();
    setAccessToken(null);
    void clearAllForLogout();
    /* Чистим React Query кеш — данные предыдущей сессии больше не нужны
       и не должны «всплывать» при следующем входе. */
    queryClient.clear();
    set({ status: 'unauthenticated', user: null });
  },

  setError: (msg) => set({ error: msg }),
}));
